import { CreateGlTextureByImageData, CreateGlTextureByPixels, DeleteTexture } from './glPipelines/utils/pipeline-utils-gl';
import { PipelineSeriesByRegionColour, PipelineType } from '@nova/types';
import type { ImageData as CanvasModImgData } from 'canvas';
import { Image } from 'canvas';

import {
  ColourMatrixTexturePipeline,
  ColourNamesPrimary,
  OverlayedTexturePipeline,
  TexturePipelineBase,
  TintedTexturePipeline
} from './glPipelines';
import { SummationTexturePipeline } from './glPipelines/pipeline-texture-summation';
import { BasePipeline } from './glPipelines/pipeline-base';
import { StaticPipelineOperations } from './StaticPipelineOps';
import { ServerTextureUtils } from './sv-texture-utils';
import { PipelineSet } from './texture-manipulation';

export type PipelineSetByColourRegion = { [colourName: string]: PipelineSet };

/**
 * This class specifically handles the creation of a region-masked texture,
 * and does so by drawing each mask-pass onto a blank texture (allowing post-masked colours to be blended during shading).
 */
export class TextureManipulationSeriesRegionMasking {
  public pipelinesByColourRegion: PipelineSetByColourRegion;
  private texDefaultPipeline: TexturePipelineBase;

  protected gl?: WebGLRenderingContext;

  // * We need to make a local copy of the base texture, since the one in the default pipeline gets replaced during every loop of
  // * "RunPipelineSeriesByRegionColour". This is just for repeat drawing.
  // * A one-time call of course would not need this cached. (e.i. from CreateFromPipelineSeriesByRegionColour)
  public outputTextureCopyCache!: WebGLTexture;
  public baseTextureCopyCache!: WebGLTexture;
  private maskGlTexture?: WebGLTexture;
  private disposablePipelines: BasePipeline[] = [];

  public constructor() {
    this.texDefaultPipeline = new TexturePipelineBase();

    this.pipelinesByColourRegion = {};
    this.pipelinesByColourRegion[ColourNamesPrimary.red] = {
      [PipelineType.textureDefault]: this.texDefaultPipeline,
      [PipelineType.tint]: new TintedTexturePipeline(ColourNamesPrimary.red),
      [PipelineType.colourMatrix]: new ColourMatrixTexturePipeline(ColourNamesPrimary.red)
    };
    this.pipelinesByColourRegion[ColourNamesPrimary.green] = {
      [PipelineType.textureDefault]: this.texDefaultPipeline,
      [PipelineType.tint]: new TintedTexturePipeline(ColourNamesPrimary.green),
      [PipelineType.colourMatrix]: new ColourMatrixTexturePipeline(ColourNamesPrimary.green)
    };
    this.pipelinesByColourRegion[ColourNamesPrimary.blue] = {
      [PipelineType.textureDefault]: this.texDefaultPipeline,
      [PipelineType.tint]: new TintedTexturePipeline(ColourNamesPrimary.blue),
      [PipelineType.colourMatrix]: new ColourMatrixTexturePipeline(ColourNamesPrimary.blue)
    };
  }

  public getGLContext(): WebGLRenderingContext | undefined {
    return this.gl;
  }

  public InitPipelines(gl: WebGLRenderingContext, canvasWidth: number, canvasHeight: number): void {
    this.gl = gl;

    for (const colourName in this.pipelinesByColourRegion) {
      if (!Object.prototype.hasOwnProperty.call(this.pipelinesByColourRegion, colourName)) { continue; }
      const pipelineSet = this.pipelinesByColourRegion[colourName];
      for (const key in pipelineSet) {
        if (!Object.prototype.hasOwnProperty.call(pipelineSet, key)) { continue; }
        const pipeline = pipelineSet[key];

        // console.log(`=== Init pipeline <${colourName}> <${key}> ===`);
        pipeline.Init(canvasWidth, canvasHeight, gl);
      }
    }
  }

  public ResizeCanvas(width: number, height: number): void {
    for (const colourName in this.pipelinesByColourRegion) {
      if (!Object.prototype.hasOwnProperty.call(this.pipelinesByColourRegion, colourName)) { continue; }
      const pipelineSet = this.pipelinesByColourRegion[colourName];
      for (const key in pipelineSet) {
        if (!Object.prototype.hasOwnProperty.call(pipelineSet, key)) { continue; }
        pipelineSet[key].SetDimensions(width, height);
      }
    }
  }

  public ClearForReuse(): void {
    // ? Since it's technically the same canvas, I think only one of these should be necessary...?
    for (const colourName in this.pipelinesByColourRegion) {
      if (!Object.prototype.hasOwnProperty.call(this.pipelinesByColourRegion, colourName)) { continue; }
      const pipelineSet = this.pipelinesByColourRegion[colourName];
      for (const key in pipelineSet) {
        if (!Object.prototype.hasOwnProperty.call(pipelineSet, key)) { continue; }
        pipelineSet[key].CleanGlState();
      }
    }
  }

  public GetPixelData(pipelineType: PipelineType, colourName: string): Uint8Array {
    const data = this.pipelinesByColourRegion[colourName][pipelineType].GetDrawnPixelData();
    if (!data) {
      throw new Error(`Could not get drawn pixel data of colourName <${colourName}>, pipelineType <${pipelineType}>`);
    }

    return data;
  }

  public Destroy(): void {
    if (this.gl) {
      DeleteTexture(this.gl, this.outputTextureCopyCache);
      DeleteTexture(this.gl, this.baseTextureCopyCache);
      DeleteTexture(this.gl, this.maskGlTexture);
    }

    for (const pipeline of this.disposablePipelines) {
      pipeline.Destroy();
    }
    this.disposablePipelines = [];

    const destroyedPipelines = new Set<TexturePipelineBase>();
    for (const colourName in this.pipelinesByColourRegion) {
      if (!Object.prototype.hasOwnProperty.call(this.pipelinesByColourRegion, colourName)) { continue; }
      const pipelineSet = this.pipelinesByColourRegion[colourName];
      for (const key in pipelineSet) {
        if (!Object.prototype.hasOwnProperty.call(pipelineSet, key)) { continue; }
        const pipeline = pipelineSet[key];
        if (destroyedPipelines.has(pipeline)) { continue; }
        pipeline.Destroy();
        destroyedPipelines.add(pipeline);
      }
      delete this.pipelinesByColourRegion[colourName];
    }
    this.gl = undefined;
    this.maskGlTexture = undefined;
    this.pipelinesByColourRegion = {};
  }

  public CreateBlankOutputTexture(width: number, height: number): void {
    if (!this.gl) {
      throw new Error(`TextureManipulationSeriesRegionMasking::CreateBlankOutputTexture: GL object not defined`);
    }
    this.outputTextureCopyCache = CreateGlTextureByPixels(this.gl, width, height, null);
  }

  public CreateBaseTexture(imgData: ImageData): void {
    if (!this.gl) {
      throw new Error(`TextureManipulationSeriesRegionMasking::CreateBaseTexture: GL object not defined`);
    }

    this.texDefaultPipeline.CreateTextures([imgData]);

    // ! This is intentionally a NEW COPY of the same base texture created just above.
    this.baseTextureCopyCache = CreateGlTextureByImageData(this.gl, imgData, this.gl.CLAMP_TO_EDGE);
  }

  public CreateMaskTexture(imgData: ImageData): void {
    if (!this.gl) {
      throw new Error(`TextureManipulationSeriesRegionMasking::CreateMaskTexture: GL object not defined`);
    }

    // We'll create this webgl texture once here because it's common to every pipeline of every draw call
    //  (making it wasteful to "create" more thna once), and only changes according to client-input.
    const maskGlTexture = CreateGlTextureByImageData(this.gl, imgData, this.gl.CLAMP_TO_EDGE);
    this.maskGlTexture = maskGlTexture;

    for (const colourName in this.pipelinesByColourRegion) {
      if (!Object.prototype.hasOwnProperty.call(this.pipelinesByColourRegion, colourName)) { continue; }
      const pipelineSet = this.pipelinesByColourRegion[colourName];
      for (const key in pipelineSet) {
        if (!Object.prototype.hasOwnProperty.call(pipelineSet, key)) { continue; }
        pipelineSet[key].regionMask?.SetTextures(this.baseTextureCopyCache, maskGlTexture);
      }
    }
  }

  public RemoveMaskTexture(): void {
    for (const colourName in this.pipelinesByColourRegion) {
      if (!Object.prototype.hasOwnProperty.call(this.pipelinesByColourRegion, colourName)) { continue; }
      const pipelineSet = this.pipelinesByColourRegion[colourName];
      for (const key in pipelineSet) {
        if (!Object.prototype.hasOwnProperty.call(pipelineSet, key)) { continue; }
        pipelineSet[key].regionMask?.RemoveTexture();
      }
    }
  }

  public ServerRepurpose(newBaseImg: Image, newMask: Image, newSeriesByRegionColour: PipelineSeriesByRegionColour): Image {
    this.ClearForReuse();

    const baseImgData = ServerTextureUtils.GetImageDataFromImage(newBaseImg);
    const maskImgData = ServerTextureUtils.GetImageDataFromImage(newMask);

    this.ResizeCanvas(newBaseImg.width, newBaseImg.height);
    this.CreateBlankOutputTexture(newBaseImg.width, newBaseImg.height);
    this.CreateBaseTexture(baseImgData as ImageData);
    this.CreateMaskTexture(maskImgData as ImageData);

    const finalPixelData: Uint8Array = this.DrawAndGetPixelData(newSeriesByRegionColour);
    const finalImageData: CanvasModImgData = ServerTextureUtils.GetImageDataFromPixels(finalPixelData, newBaseImg.width, newBaseImg.height);

    return ServerTextureUtils.GetImageFromImageData(finalImageData);
  }

  public ServerDestroy(): void {
    this.Destroy();
    ServerTextureUtils.DestroyCanvas();
  }

  private DrawInternal(seriesByRegionColour: PipelineSeriesByRegionColour): OverlayedTexturePipeline {
    if (!this.gl) { throw new Error('gl is missing!!'); }

    for (const pipeline of this.disposablePipelines) {
      pipeline.Destroy();
    }
    this.disposablePipelines = [];

    this.texDefaultPipeline.SetGlTextures([this.outputTextureCopyCache]);

    const sumPipeline = new SummationTexturePipeline();
    const { canvasWidth, canvasHeight, Gl } = this.texDefaultPipeline;
    sumPipeline.Init(canvasWidth, canvasHeight, Gl);
    this.disposablePipelines.push(sumPipeline);

    const textureResult: WebGLTexture | undefined =
      StaticPipelineOperations.RunPipelineSeriesByRegionColour(this.pipelinesByColourRegion, sumPipeline, seriesByRegionColour, this.baseTextureCopyCache);

    const overlayPipeline = new OverlayedTexturePipeline();
    overlayPipeline.Init(canvasWidth, canvasHeight, this.gl);
    this.disposablePipelines.push(overlayPipeline);

    overlayPipeline.SetGlTextures([this.baseTextureCopyCache, textureResult || this.outputTextureCopyCache]);
    overlayPipeline.UseProgram();
    overlayPipeline.Draw({});

    if (textureResult && textureResult !== this.outputTextureCopyCache && textureResult !== this.baseTextureCopyCache) {
      DeleteTexture(this.gl, textureResult);
    }

    return overlayPipeline;
  }

  public Draw(seriesByRegionColour: PipelineSeriesByRegionColour): void {
    this.DrawInternal(seriesByRegionColour);
  }

  public DrawAndGetPixelData(seriesByRegionColour: PipelineSeriesByRegionColour): Uint8Array {
    const overlayPipeline: OverlayedTexturePipeline = this.DrawInternal(seriesByRegionColour);
    return overlayPipeline.GetDrawnPixelData();
  }
}
